<template>
  <div>
    <!-- Billing provider filter -->
    <div class="webhook-provider-filter" data-testid="webhook-provider-filter">
      <button
        v-for="opt in providerOptions"
        :key="opt.value"
        :class="['webhook-provider-btn', { active: activeProvider === opt.value }]"
        :data-testid="`webhook-filter-provider-${opt.value}`"
        @click="setProvider(opt.value)"
      >
        {{ opt.label }}
      </button>
    </div>

    <data-table
      data-testid="webhook-logs-table"
      :config="datatableConfig"
      :data="logs"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @refresh="handleRefresh">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import DataTable from '../../grid/DataTable.vue';
import { useWebhookLogs } from '../../composables/useWebhookLogs';

type ProviderFilter = 'all' | 'gatewaytest' | 'stripe' | 'asaas';

type Id = number | string;

interface CellParams {
  data: Record<string, unknown>;
  value?: unknown;
}

interface WebhookRow {
  id?: Id;
  [key: string]: unknown;
}

const { t } = useI18n();

// The composable owns the network read. This screen deliberately does NOT use
// DataTable's `endpoint` loader: that loader treats `dados` as the row array,
// while WebhookLogs_BuscarResponse wraps it as `{ logs: [...] }` — every load
// threw `data.map is not a function`, blanking the table and firing the error
// toast. `:data` takes the already-unwrapped rows.
const { logs, fetchLogs } = useWebhookLogs();

// ── Provider filter ───────────────────────────────────────────────────────────
// The values below are WIRE values, not labels: they go out as ?provider=<value>
// and must match the provider_code the receiver stores. Webhooks::gatewaytest()
// stores 'gatewaytest' (S3 rename), so filtering by the old 'fake' returned an
// empty list against a table that had rows — silent, and worse than an error.

const activeProvider = ref<ProviderFilter>('all');

const providerOptions = computed(() => [
  { value: 'all' as ProviderFilter, label: t('Todos') },
  { value: 'gatewaytest' as ProviderFilter, label: t('GatewayTest (dev)') },
  { value: 'stripe' as ProviderFilter, label: 'Stripe' },
  { value: 'asaas' as ProviderFilter, label: 'Asaas' },
]);

function setProvider(value: ProviderFilter): void {
  activeProvider.value = value;
  // Pass the value straight through instead of letting a child re-read a
  // computed prop: the previous wiring called loadData() on the same tick the
  // ref changed, so the request carried the PREVIOUS provider (or none).
  void loadLogs(value);
}

const handleWebhookClick = (value: Id | undefined, rowData: WebhookRow | undefined): void => {
  log('WebhookLogsTable', 'handleWebhookClick', 'Opening webhook detail', rowData);
  eventBus.emit('modal.webhookdetail.open', { webhook: rowData });
};

const truncate = (value: unknown, max: number): string => {
  if (value == null || value === '') return '';
  const str = String(value);
  return str.length > max ? str.substring(0, max) + '...' : str;
};

const tableColumns = computed(() => {
  return [
    {
      data: 'id',
      title: t('ID'),
      width: 80,
      sortable: true,
      filterType: 'text',
      renderType: 'custom_clickable_id',
      clickCallback: handleWebhookClick
    },
    {
      data: 'created_at',
      title: t('Data'),
      width: 180,
      sortable: true,
      filterType: 'text',
      renderType: 'datetime'
    },
    {
      data: 'event_type',
      title: t('Tipo de Evento'),
      width: 180,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'provider',
      title: t('Provedor'),
      width: 120,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'status',
      title: t('Status'),
      width: 120,
      sortable: true,
      filterType: 'text',
      cellRenderer: (params: CellParams): string => {
        if (params.value == null || params.value === '') return '';
        const value = String(params.value);
        const statusMap: Record<string, string> = {
          'processed': `<span class="status-badge status-success">${t('Processado')}</span>`,
          'failed': `<span class="status-badge status-error">${t('Falhou')}</span>`,
          'received': `<span class="status-badge status-pending">${t('Recebido')}</span>`,
          'ignored': `<span class="status-badge status-ignored">${t('Ignorado')}</span>`
        };
        return statusMap[value.toLowerCase()] || value;
      }
    },
    {
      data: 'error_message',
      title: t('Mensagem de Erro'),
      width: 250,
      sortable: false,
      filterType: 'text',
      cellRenderer: (params: CellParams): string => truncate(params.value, 100)
    },
    {
      data: 'payload',
      title: t('Payload'),
      width: 400,
      sortable: false,
      filterType: 'text',
      cellRenderer: (params: CellParams): string => {
        if (params.value == null || params.value === '') return '';
        try {
          const jsonObj = typeof params.value === 'string' ? JSON.parse(params.value) : params.value;
          const jsonStr = JSON.stringify(jsonObj);
          return jsonStr.length > 150 ? jsonStr.substring(0, 150) + '...' : jsonStr;
        } catch {
          return truncate(params.value, 150);
        }
      }
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `webhook_logs_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 100,
    pagination: true,
    rowSelection: null,
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => ({
  show: true,
  text: t('Atualizar')
}));

const csvButtonConfig = computed(() => ({
  show: true,
  text: t('Exportar CSV')
}));

const loadLogs = async (provider: ProviderFilter): Promise<void> => {
  log('WebhookLogsTable', 'loadLogs', 'Triggering data reload', { provider });
  try {
    await fetchLogs(provider);
  } catch (error) {
    log('WebhookLogsTable', 'loadLogs', 'Error loading data', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar logs de webhook'));
  }
};

const loadData = (): void => {
  void loadLogs(activeProvider.value);
};

const handleRefresh = (): void => {
  loadData();
};

onMounted(() => {
  log('WebhookLogsTable', 'mounted', 'Component mounted');
  eventBus.on('webhook-logs.refresh', handleRefresh);
  loadData();
});

onBeforeUnmount(() => {
  eventBus.off('webhook-logs.refresh', handleRefresh);
});

defineExpose({
  loadData
});
</script>

<style scoped>
.webhook-provider-filter {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.webhook-provider-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.8rem;
  border: 1px solid var(--p-surface-300, #cbd5e1);
  border-radius: 0.375rem;
  background: var(--p-surface-0, #fff);
  color: var(--p-text-color, #1e293b);
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.webhook-provider-btn:hover {
  background: var(--p-surface-100, #f1f5f9);
}

.webhook-provider-btn.active {
  background: var(--p-primary-color, #2563eb);
  border-color: var(--p-primary-color, #2563eb);
  color: #fff;
}

:deep(.status-badge) {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

:deep(.status-success) {
  background-color: var(--p-green-100);
  color: var(--p-green-700);
}

:deep(.status-error) {
  background-color: var(--p-red-100);
  color: var(--p-red-700);
}

:deep(.status-pending) {
  background-color: var(--p-yellow-100);
  color: var(--p-yellow-700);
}

:deep(.status-ignored) {
  background-color: var(--p-gray-100);
  color: var(--p-gray-600);
}
</style>
